<template>
  <v-card v-bind:class="actionClass">
    <!-- Pictures meta info -->
    <v-toolbar flat color="#f5fff5">
      <v-toolbar-title class="blue-grey--text">
        <span class="subtitle-1">{{ $t('image') }}</span>
        <span class="caption pl-2">LVL {{ level }}</span>
        <span class="caption pl-2">IDX {{ index }}</span>
      </v-toolbar-title>
      <v-spacer class="handle"></v-spacer>
      <v-menu open-on-hover left offset-x v-if="toolbarEnabled">
        <template v-slot:activator="{ on }">
          <v-btn icon class="caption" v-on="on">
            <v-icon>more_vert</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-btn text icon color="error" :title="$t('remove')" @click="remove">
            <v-icon>delete_forever</v-icon>
          </v-btn>
        </v-list>
      </v-menu>
    </v-toolbar>
    <!-- Picture fields editor -->
    <v-container fluid>
      <v-layout>
        <v-flex>
          <v-text-field v-model="localLabel" :label="$t('label')"></v-text-field>
        </v-flex>
        <v-flex v-if="selectedLanguage" class="ml-3">
          <v-text-field v-model="localTranslatedLabel" :label="translatedLabel"></v-text-field>
        </v-flex>
      </v-layout>
      <v-layout>
        <v-flex>
          <v-text-field v-model="localDescription" :label="$t('description')"></v-text-field>
        </v-flex>
        <v-flex v-if="selectedLanguage" class="ml-3">
          <v-text-field v-model="localTranslatedDescription" :label="translatedDescription"></v-text-field>
        </v-flex>
      </v-layout>
    </v-container>
    <!-- Picture editor -->
    <v-container fluid grid-list-xs class="pt-0">
      <v-layout row wrap>
        <v-flex v-for="id in items" :key="id" class="xs12 sm6 md4 lg3 xl2">
          <v-img contain
                v-if="images[id]"
                :src="images[id]"
                class="outline"
                height="200">
            <v-layout class="stripe">
              <v-flex class="pa-3">
                {{ getImageFilename(id) }}
              </v-flex>
              <v-flex text-right v-if="toolbarEnabled">
                <v-btn icon text @click="removeImage(id)" color="red darken-2" :title="$t('remove')">
                  <v-icon>delete_forever</v-icon>
                </v-btn>
              </v-flex>
            </v-layout>
          </v-img>
        </v-flex>
        <v-flex text-center v-if="toolbarEnabled">
          <form v-if="checklistId" enctype="multipart/form-data" justify-center>
            <input ref="fileInput" name="pictureUploadInput" type="file" accept="image/*" class="outline pa-1"/>
            <v-btn @click.stop="saveImage" :loading="uploading" :disabled="uploading">{{ $t('upload') }}</v-btn>
          </form>
          <v-alert v-else type="info" color="grey lighten-4 black--text">
            {{ $t('checklists.save-to-enable-image-upload') }}
          </v-alert>
        </v-flex>
      </v-layout>
    </v-container>
  </v-card>
</template>

<style scoped>
  .outline {
    border: 1px solid #eee;
    border-radius: 2px;
  }
  .stripe {
    background: rgba(255, 255, 255, 0.33);
  }
</style>

<script>
import editorMixins from './editor-mixins.es6'

export default {
  name: 'editor-image',
  mixins: [editorMixins],
  props: {
    level: Number,
    index: Number,
    type: String,
    label: String,
    items: Array
  },
  data () {
    return {
      uploading: false
    }
  },
  computed: {
    checklistId () {
      return this.$store.getters['checklist/checklistId']
    },
    nextImageIndex () {
      return this.items.length + 1
    },
    images () {
      const size = 'medium'
      const data = []
      const pics = this.$store.state.files

      Object.keys(pics).forEach((id) => {
        if (pics[id] && pics[id][size]) data[Number(id)] = pics[id][size]
      })

      return data
    }
  },
  methods: {
    async saveImage () {
      const fileInput = this.$refs.fileInput

      if (!fileInput.files.length) {
        return // no file selected
      }

      this.uploading = true

      const file = fileInput.files[0]
      const data = new FormData()
      data.append('file', file)

      this.eventBus.$emit('uploadImage', {
        idx: this.index,
        items: [...this.items],
        data
      })
    },
    async removeImage (id) {
      this.eventBus.$emit('removeImage', {
        idx: this.index,
        items: [...this.items],
        id
      })
    },
    async loadImages () {
      try {
        await Promise.all(this.items.map((id) => this.getImageSource(id)))
      } catch (error) {
        console.error(error)
      }
    },
    async getImageSource (id, size = 'medium') {
      try {
        return this.$store.dispatch('checklist/fetchImage', {
          id,
          requiredSize: size
        })
      } catch (error) {
        return Promise.reject(error)
      }
    },
    getImageFilename (id) {
      const file = this.$store.state.items.files.filter((f) => f.id === id).shift()
      return file ? file.path : ''
    }
  },
  watch: {
    items () {
      // load newly added image
      this.uploading = false
      this.loadImages()
    }
  },
  created () {
    // load current images
    if (this.items.length) {
      this.loadImages()
    }
  }
}
</script>
